import React from 'react';
import { motion } from 'framer-motion';
import { Header } from '@/components/Header';
import { SignupForm } from '@/components/SignupForm';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  FloatingElements,
  CardIllustration,
  RewardBadge,
  StepCard,
  CryptoIcons,
  PaymentIcons
} from '@/components/AnimatedElements';
import { CreditCard, Wallet, ShoppingBag, Sparkles } from 'lucide-react';

const HeroSection = () => {
  const { t } = useLanguage();
  return (
    <section className="relative min-h-screen pt-20 pb-12 overflow-hidden">
      <FloatingElements />

      <div className="relative z-10 max-w-lg mx-auto px-4">
        {/* Hero Content */}
        <div className="pt-8 pb-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t('hero.limitedTime')}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl font-display font-bold text-foreground leading-tight mb-4"
          >
            {t('hero.spendCrypto')}{' '}
            <span className="text-gradient-primary">{t('hero.likeCash')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-muted-foreground text-lg mb-6"
          >
            {t('hero.registrationReward')}
          </motion.p>

          {/* Payment Icons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="flex flex-col items-center gap-6 mb-8"
          >
            <PaymentIcons />

            {/* Play Store Link */}
            <motion.a
              href="https://play.google.com/store/apps/details?id=io.blonix.app&hl=en_IN"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-3 px-6 py-3 rounded-2xl glass-strong border border-primary/20 hover:border-primary/40 transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                  <path d="M5.33031 3.1901C4.94589 3.01327 4.54922 2.94692 4.19531 3.01327L13.8242 12.5539L18.4731 7.94982L5.33031 3.1901Z" fill="#00E676" />
                  <path d="M4.19531 3.01327C3.79973 3.09062 3.42473 3.32262 3.14139 3.69837C2.97864 3.91187 2.89531 4.16597 2.89531 4.43122V19.7212C2.89531 19.9865 2.97864 20.2406 3.14139 20.4541C3.42473 20.8299 3.79973 21.0619 4.19531 21.1392C4.54922 21.2056 4.94589 21.1392 5.33031 20.9624L13.8242 12.5539L4.19531 3.01327Z" fill="#2196F3" />
                  <path d="M5.33031 20.9624L13.8242 12.5539L18.4731 17.158L5.33031 20.9624Z" fill="#FFC107" />
                  <path d="M18.4731 7.94982L13.8242 12.5539L20.1704 18.843C20.671 18.3347 21.0044 17.5833 21.0544 16.633V7.52992C21.0044 6.57967 20.671 5.82827 20.1704 5.31992L18.4731 7.94982Z" fill="#F44336" />
                </svg>
              </div>
              <div className="flex flex-col items-start leading-none">
                <span className="text-[10px] text-muted-foreground uppercase font-semibold tracking-wider mb-0.5">
                  GET IT ON
                </span>
                <span className="text-foreground font-display font-bold text-lg leading-none">
                  Google Play
                </span>
              </div>
            </motion.a>
          </motion.div>
        </div>

        {/* Card Illustration */}
        <div className="flex justify-center mb-8">
          <CardIllustration />
        </div>

        {/* Reward Badge */}
        <div className="flex justify-center mb-8">
          <RewardBadge amount="$10" currency="USDT" />
        </div>

        {/* Signup Form */}
        <div className="glass-strong rounded-3xl p-6 mb-8">
          <SignupForm />
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-16 px-4">
      <div className="max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-3">
            {t('features.title')} <span className="text-gradient-primary">{t('features.subtitle')}</span>
          </h2>
          <p className="text-muted-foreground">
            {t('features.description')}
          </p>
        </motion.div>

        {/* Feature Cards */}
        <div className="grid gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-4 p-4 rounded-2xl gradient-card border border-border/50"
          >
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
              <CreditCard className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h3 className="text-foreground font-semibold mb-0.5">{t('features.virtualPhysical.title')}</h3>
              <p className="text-muted-foreground text-sm">{t('features.virtualPhysical.description')}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-4 p-4 rounded-2xl gradient-card border border-border/50"
          >
            <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center">
              <Wallet className="w-7 h-7 text-secondary" />
            </div>
            <div>
              <h3 className="text-foreground font-semibold mb-0.5">{t('features.multiCrypto.title')}</h3>
              <div className="mt-1">
                <CryptoIcons />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-4 p-4 rounded-2xl gradient-card border border-border/50"
          >
            <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-accent" />
            </div>
            <div>
              <h3 className="text-foreground font-semibold mb-0.5">{t('features.spendAnywhere.title')}</h3>
              <p className="text-muted-foreground text-sm">{t('features.spendAnywhere.description')}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const StepsSection = () => {
  const { t } = useLanguage();
  const steps = [
    {
      number: 1,
      title: t('steps.step1.title'),
      description: t('steps.step1.description'),
      icon: <CreditCard className="w-6 h-6" />
    },
    {
      number: 2,
      title: t('steps.step2.title'),
      description: t('steps.step2.description'),
      icon: <Wallet className="w-6 h-6" />
    },
    {
      number: 3,
      title: t('steps.step3.title'),
      description: t('steps.step3.description'),
      icon: <ShoppingBag className="w-6 h-6" />
    }
  ];

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
      <div className="max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-3">
            {t('steps.title')} <span className="text-gradient-primary">{t('steps.subtitle')}</span>
          </h2>
          <p className="text-muted-foreground">
            {t('steps.description')}
          </p>
        </motion.div>

        <div className="space-y-4">
          {steps.map((step, index) => (
            <StepCard
              key={step.number}
              number={step.number}
              title={step.title}
              description={step.description}
              icon={step.icon}
              delay={index * 0.15}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-16 px-4">
      <div className="max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-3xl gradient-primary p-8 text-center overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-foreground/20 -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-foreground/20 translate-y-1/2 -translate-x-1/4" />
          </div>

          <div className="relative z-10">
            <motion.div
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="inline-flex items-center gap-2 mb-4"
            >
              <div className="w-12 h-12 rounded-full gradient-gold flex items-center justify-center shadow-lg">
                <span className="text-accent-foreground font-bold text-xl">$</span>
              </div>
            </motion.div>

            <h2 className="text-2xl sm:text-3xl font-display font-bold text-primary-foreground mb-3">
              {t('cta.title1')}
            </h2>
            <div className="text-4xl sm:text-5xl font-display font-bold text-primary-foreground mb-4">
              {t('cta.reward')}
            </div>
            <p className="text-primary-foreground/80 mb-6">
              {t('cta.description')}
            </p>

            <motion.a
              href="#top"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-card text-foreground font-semibold shadow-lg hover:shadow-xl transition-shadow"
            >
              {t('cta.button')}
              <Sparkles className="w-5 h-5 text-primary" />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="py-8 px-4 border-t border-border/50">
      <div className="max-w-lg mx-auto">
        <div className="flex flex-col items-center gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg font-display">B</span>
            </div>
            <span className="text-foreground font-display font-semibold text-xl">Blonix</span>
          </div>

          <motion.a
            href="https://play.google.com/store/apps/details?id=io.blonix.app&hl=en_IN"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 border border-border/50 text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
              <path d="M5.33031 3.1901C4.94589 3.01327 4.54922 2.94692 4.19531 3.01327L13.8242 12.5539L18.4731 7.94982L5.33031 3.1901Z" fill="#00E676" />
              <path d="M4.19531 3.01327C3.79973 3.09062 3.42473 3.32262 3.14139 3.69837C2.97864 3.91187 2.89531 4.16597 2.89531 4.43122V19.7212C2.89531 19.9865 2.97864 20.2406 3.14139 20.4541C3.42473 20.8299 3.79973 21.0619 4.19531 21.1392C4.54922 21.2056 4.94589 21.1392 5.33031 20.9624L13.8242 12.5539L4.19531 3.01327Z" fill="#2196F3" />
              <path d="M5.33031 20.9624L13.8242 12.5539L18.4731 17.158L5.33031 20.9624Z" fill="#FFC107" />
              <path d="M18.4731 7.94982L13.8242 12.5539L20.1704 18.843C20.671 18.3347 21.0044 17.5833 21.0544 16.633V7.52992C21.0044 6.57967 20.671 5.82827 20.1704 5.31992L18.4731 7.94982Z" fill="#F44336" />
            </svg>
            {t('footer.playStore')}
          </motion.a>



          {/* Copyright */}
          <p className="text-muted-foreground text-xs">
            {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export const InvitePage = () => {
  return (
    <div id="top" className="min-h-screen bg-background">
      <Header />
      <HeroSection />
      <FeaturesSection />
      <StepsSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default InvitePage;
